import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { ComponentLinesEditor, StoredLine } from '../../components/manufacturing/BomStructureModal';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'draft' | 'submitted' | 'approved' | 'implemented' | 'rejected' | 'cancelled';
type ChangeType = 'engineering' | 'cost' | 'quality' | 'supplier';

interface EcnRow {
  id: string;
  ecnNumber: string;
  bomNumber: string;
  productName: string;
  changeType: ChangeType;
  title: string;
  status: Status;
  fromRevision: string;
  toRevision: string | null;
  proposedEffectiveDate: string | null;
  createdAt: string;
}
interface EcnPage {
  data: EcnRow[];
  total: number;
  totalPages: number;
}
interface Difference {
  kind: 'added' | 'removed' | 'changed';
  componentId: string;
  sku: string;
  name: string;
  unit: string;
  changes: string[];
}
interface Ecn extends EcnRow {
  bomHeaderId: string;
  productId: string | null;
  description: string | null;
  reason: string | null;
  previousLines: StoredLine[];
  proposedLines: StoredLine[];
  difference: Difference[];
  costBefore: number;
  costAfter: number;
  bomRevision: string | null;
  requestedBy: string | null;
  decidedBy: string | null;
  implementedBy: string | null;
  decisionComments: string | null;
  cancelReason: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  implementedAt: string | null;
  openWorkOrders: { id: number; workOrderNumber: string; status: string; plannedQuantity: number }[];
}
interface Summary {
  drafts: number;
  awaitingApproval: number;
  awaitingImplementation: number;
  implementedThisMonth: number;
}
interface ChangeableBom {
  id: string;
  bomNumber: string;
  revision: string;
  productName: string;
  sku: string;
  openEcnNumber: string | null;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  submitted: { label: 'Awaiting approval', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  approved: { label: 'Approved', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  implemented: { label: 'Implemented', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const TYPES: Record<ChangeType, string> = { engineering: 'Engineering', cost: 'Cost', quality: 'Quality', supplier: 'Supplier' };
const DIFFERENCE: Record<Difference['kind'], { label: string; className: string }> = {
  added: { label: 'Added', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  removed: { label: 'Removed', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  changed: { label: 'Changed', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
};

const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const ecnApi = {
  page: async (params: object): Promise<EcnPage> => (await api.get('/ecns', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/ecns/summary')).data,
  boms: async (search: string): Promise<ChangeableBom[]> => (await api.get('/ecns/changeable-boms', { params: clean({ search }) })).data,
  one: async (id: string): Promise<Ecn> => (await api.get(`/ecns/${id}`)).data,
  create: async (data: object): Promise<Ecn> => (await api.post('/ecns', data)).data,
  update: async (id: string, data: object): Promise<Ecn> => (await api.patch(`/ecns/${id}`, data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Ecn> => (await api.post(`/ecns/${id}/${action}`, data)).data,
};

// ---------------------------------------------------------------- raise a note

const RaiseForm: React.FC<{ bomId: string; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ bomId, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ bomHeaderId: bomId, title: '', reason: '', changeType: 'engineering' as ChangeType, proposedEffectiveDate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: boms = [], isLoading } = useQuery({ queryKey: ['ecns', 'boms'], queryFn: () => ecnApi.boms('') });
  const chosen = boms.find((bom) => bom.id === form.bomHeaderId);
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.bomHeaderId) next.bomHeaderId = 'Choose the BOM to change';
    else if (chosen?.openEcnNumber) next.bomHeaderId = `${chosen.openEcnNumber} is already open on this BOM`;
    if (form.title.trim().length < 5) next.title = 'Say in a few words what the change is';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ bomHeaderId: form.bomHeaderId, title: form.title.trim(), reason: form.reason.trim() || null, changeType: form.changeType, proposedEffectiveDate: form.proposedEffectiveDate || null });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <Field label="BOM" htmlFor="ec-bom" required error={errors.bomHeaderId} hint={!isLoading && boms.length === 0 ? 'No BOM is approved yet. A draft BOM is changed directly.' : undefined}>
        <select id="ec-bom" value={form.bomHeaderId} onChange={set('bomHeaderId')} className={cn(inputClass, errors.bomHeaderId && inputErrorClass)}>
          <option value="">{isLoading ? 'Loading…' : 'Choose…'}</option>
          {boms.map((bom) => (
            <option key={bom.id} value={bom.id}>
              {bom.bomNumber} · rev {bom.revision} · {bom.productName}
              {bom.openEcnNumber ? ` (${bom.openEcnNumber} open)` : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="What is the change?" htmlFor="ec-title" required error={errors.title}>
        <input id="ec-title" type="text" maxLength={200} value={form.title} onChange={set('title')} className={cn(inputClass, errors.title && inputErrorClass)} placeholder="Replace the M8 bolt by a stainless one" />
      </Field>
      <Field label="Why is it needed?" htmlFor="ec-reason" hint="Needed before the note is sent for approval">
        <textarea id="ec-reason" rows={2} maxLength={4000} value={form.reason} onChange={set('reason')} className={inputClass} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Kind of change" htmlFor="ec-type">
          <select id="ec-type" value={form.changeType} onChange={set('changeType')} className={inputClass}>
            {(Object.keys(TYPES) as ChangeType[]).map((type) => (<option key={type} value={type}>{TYPES[type]}</option>))}
          </select>
        </Field>
        <Field label="Wanted from" htmlFor="ec-date" hint="The BOM changes on the day the note is implemented">
          <input id="ec-date" type="date" value={form.proposedEffectiveDate} onChange={set('proposedEffectiveDate')} className={inputClass} />
        </Field>
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Raising…' : 'Raise Change Note'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one note

const LinesTable: React.FC<{ lines: StoredLine[] }> = ({ lines }) =>
  lines.length === 0 ? (
    <p className="px-3 py-3 text-sm text-gray-500">No components.</p>
  ) : (
    <ul className="divide-y divide-gray-100">
      {lines.map((line, index) => (
        <li key={`${line.componentId}-${index}`} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
          <span className="text-gray-900">
            {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
            {line.isPhantom && <span className={cn(badgeClass, 'ml-2 bg-purple-50 text-purple-700 ring-purple-600/20')}>Phantom</span>}
            {line.isOptional && <span className={cn(badgeClass, 'ml-2 bg-gray-50 text-gray-700 ring-gray-500/20')}>Optional</span>}
            {line.alternativeGroup && <span className="ml-2 text-xs text-gray-500">alt. {line.alternativeGroup}/{line.alternativeRank}</span>}
          </span>
          <span className="shrink-0 text-gray-900 tabular-nums">
            {formatNumber(line.quantity, 6)} <span className="text-gray-500">{line.unit}</span>
            {line.scrapPercentage > 0 && <span className="text-xs text-gray-500"> +{formatNumber(line.scrapPercentage)}% scrap</span>}
          </span>
        </li>
      ))}
    </ul>
  );

interface PanelProps {
  ecnId: string;
  canEdit: boolean;
  canApprove: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const EcnPanel: React.FC<PanelProps> = ({ ecnId, canEdit, canApprove, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: ecn, isLoading, isError, error, dataUpdatedAt } = useQuery({ queryKey: ['ecns', 'one', ecnId], queryFn: () => ecnApi.one(ecnId) });
  const [reason, setReason] = useState('');
  // Which action is asking for a comment or a reason, if any
  const [asking, setAsking] = useState<'approve' | 'reject' | 'cancel' | null>(null);
  const [comment, setComment] = useState('');
  useEffect(() => {
    if (ecn) setReason(ecn.reason ?? '');
  }, [ecn]);

  const done = (message: string) => (saved: Ecn) => {
    toast.success(message.replace('{n}', saved.ecnNumber).replace('{r}', saved.toRevision ?? ''));
    queryClient.setQueryData(['ecns', 'one', ecnId], saved);
    setAsking(null);
    setComment('');
    onChanged();
  };
  const fail = (fallback: string) => (actionError: unknown) => toast.error(getApiErrorMessage(actionError, fallback));
  const saveLines = useMutation({ mutationFn: (lines: object[]) => ecnApi.update(ecnId, { lines, reason: reason.trim() || null }), onSuccess: done('Proposal of {n} saved'), onError: fail('The proposal could not be saved') });
  const submit = useMutation({ mutationFn: async () => { await ecnApi.update(ecnId, { reason: reason.trim() || null }); return ecnApi.act(ecnId, 'submit'); }, onSuccess: done('{n} sent for approval'), onError: fail('The note could not be sent') });
  const approve = useMutation({ mutationFn: () => ecnApi.act(ecnId, 'approve', { comments: comment.trim() || null }), onSuccess: done('{n} approved'), onError: fail('The note could not be approved') });
  const reject = useMutation({ mutationFn: () => ecnApi.act(ecnId, 'reject', { comments: comment.trim() }), onSuccess: done('{n} rejected'), onError: fail('The note could not be rejected') });
  const implement = useMutation({ mutationFn: () => ecnApi.act(ecnId, 'implement'), onSuccess: done('{n} implemented: the BOM is now revision {r}'), onError: fail('The change could not be implemented') });
  const cancel = useMutation({ mutationFn: () => ecnApi.act(ecnId, 'cancel', { reason: comment.trim() }), onSuccess: done('{n} cancelled'), onError: fail('The note could not be cancelled') });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !ecn) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The note could not be loaded.')}</p>;

  const draft = ecn.status === 'draft';
  const open = draft || ecn.status === 'submitted' || ecn.status === 'approved';
  const busy = submit.isPending || approve.isPending || reject.isPending || implement.isPending || cancel.isPending;

  return (
    <div className="space-y-4">
      <DetailCard title={ecn.title}>
        <DetailItem label="BOM">
          {ecn.bomNumber} · rev {ecn.fromRevision}
          {ecn.toRevision ? ` → ${ecn.toRevision}` : ''}
        </DetailItem>
        <DetailItem label="Product">{ecn.productName}</DetailItem>
        <DetailItem label="Kind">{TYPES[ecn.changeType]}</DetailItem>
        <DetailItem label="Status"><span className={cn(badgeClass, STATUS[ecn.status].className)}>{STATUS[ecn.status].label}</span></DetailItem>
        <DetailItem label="Raised">{[formatDate(ecn.createdAt), ecn.requestedBy].filter(Boolean).join(' · ')}</DetailItem>
        <DetailItem label="Wanted from">{formatDate(ecn.proposedEffectiveDate)}</DetailItem>
        <DetailItem label={ecn.status === 'rejected' ? 'Rejected' : 'Approved'}>{ecn.decidedAt ? [formatDate(ecn.decidedAt), ecn.decidedBy].filter(Boolean).join(' · ') : '—'}</DetailItem>
        <DetailItem label="Implemented">{ecn.implementedAt ? [formatDate(ecn.implementedAt), ecn.implementedBy].filter(Boolean).join(' · ') : '—'}</DetailItem>
        {(ecn.decisionComments || ecn.cancelReason) && (
          <DetailItem label={ecn.cancelReason ? 'Cancelled because' : 'Comments'} className="col-span-2 lg:col-span-4"><span className="font-normal">{ecn.cancelReason || ecn.decisionComments}</span></DetailItem>
        )}
      </DetailCard>

      <Field label="Why is it needed?" htmlFor="ep-reason">
        <textarea id="ep-reason" rows={2} maxLength={4000} disabled={!draft || !canEdit} value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
      </Field>

      <section className="rounded-lg border border-gray-200">
        <h4 className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>What changes · {formatNumber(ecn.difference.length)}</span>
          <span className="normal-case tracking-normal text-gray-600">
            Material cost {formatMoney(ecn.costBefore)} → {formatMoney(ecn.costAfter)}
          </span>
        </h4>
        {ecn.difference.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">{draft ? 'Nothing yet. Change the components below and save the proposal.' : 'No difference.'}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {ecn.difference.map((item, index) => (
              <li key={`${item.componentId}-${index}`} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                <span className={cn(badgeClass, DIFFERENCE[item.kind].className)}>{DIFFERENCE[item.kind].label}</span>
                <span className="text-gray-900">
                  {item.name} <span className="text-xs text-gray-500">{item.sku}</span>
                </span>
                <span className="text-gray-600">{item.changes.join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {draft && canEdit ? (
        <section className="rounded-lg border border-gray-200 p-3">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Components after the change</h4>
          <ComponentLinesEditor
            key={dataUpdatedAt}
            structure={{ productId: ecn.productId ?? '', productName: ecn.productName, unit: null, baseQuantity: 1, lines: ecn.proposedLines }}
            canEdit
            isPending={saveLines.isPending}
            saveLabel="Save Proposal"
            onSave={(lines) => saveLines.mutate(lines)}
          />
        </section>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section className="overflow-hidden rounded-lg border border-gray-200">
            <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Before · rev {ecn.fromRevision}</h4>
            <LinesTable lines={ecn.previousLines} />
          </section>
          <section className="overflow-hidden rounded-lg border border-gray-200">
            <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">After{ecn.toRevision ? ` · rev ${ecn.toRevision}` : ''}</h4>
            <LinesTable lines={ecn.proposedLines} />
          </section>
        </div>
      )}

      {open && ecn.openWorkOrders.length > 0 && (
        <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 ring-1 ring-gray-200">
          {ecn.openWorkOrders.length} open work order(s) were made from this BOM ({ecn.openWorkOrders.map((order) => order.workOrderNumber).join(', ')}). They keep the materials they were created with; the change applies to work orders made after it is implemented.
        </p>
      )}

      {asking && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label={asking === 'approve' ? 'Comments (optional)' : asking === 'reject' ? 'Why is it rejected?' : 'Why is it cancelled?'} htmlFor="ep-comment" required={asking !== 'approve'}>
            <input id="ep-comment" type="text" maxLength={asking === 'cancel' ? 300 : 500} value={comment} onChange={(event) => setComment(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setAsking(null); setComment(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button
              type="button"
              disabled={busy || (asking !== 'approve' && comment.trim().length < 3)}
              onClick={() => (asking === 'approve' ? approve.mutate() : asking === 'reject' ? reject.mutate() : cancel.mutate())}
              className={asking === 'approve' ? primaryButtonClass : dangerButtonClass}
            >
              {asking === 'approve' ? 'Confirm Approval' : asking === 'reject' ? 'Confirm Rejection' : 'Confirm Cancelling'}
            </button>
          </div>
        </div>
      )}

      {!asking && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {open && canEdit && (
            <button type="button" onClick={() => setAsking('cancel')} disabled={busy} className={secondaryButtonClass}>
              Cancel Note
            </button>
          )}
          {draft && canEdit && (
            <button type="button" onClick={() => submit.mutate()} disabled={busy || ecn.difference.length === 0 || reason.trim().length === 0} title={ecn.difference.length === 0 ? 'Save a proposal that changes something first' : reason.trim().length === 0 ? 'Say why the change is needed' : undefined} className={primaryButtonClass}>
              {submit.isPending ? 'Sending…' : 'Send for Approval'}
            </button>
          )}
          {ecn.status === 'submitted' && canApprove && (
            <>
              <button type="button" onClick={() => setAsking('reject')} disabled={busy} className={dangerButtonClass}>
                Reject
              </button>
              <button type="button" onClick={() => setAsking('approve')} disabled={busy} className={primaryButtonClass}>
                Approve
              </button>
            </>
          )}
          {ecn.status === 'approved' && canApprove && (
            <button type="button" onClick={() => implement.mutate()} disabled={busy} className={primaryButtonClass}>
              {implement.isPending ? 'Implementing…' : 'Implement on the BOM'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const EngineeringChanges: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('master_data.create');
  const canEdit = can('master_data.update');
  const canApprove = can('master_data.approve');
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const bomFromLink = searchParams.get('bom') ?? '';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(!!bomFromLink);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['ecns', 'page', { page, limit, search, status: statusFilter }],
    queryFn: () => ecnApi.page({ page, limit, search, status: statusFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['ecns', 'summary'], queryFn: ecnApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['ecns', 'one', viewingId], queryFn: () => ecnApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => {
    ['page', 'summary', 'boms'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['ecns', key] }));
    ['engineeringBoms', 'bomHeaders'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  };
  const closeForm = () => {
    setFormOpen(false);
    if (bomFromLink) setSearchParams({}, { replace: true });
  };
  const create = useMutation({
    mutationFn: ecnApi.create,
    onSuccess: (ecn) => {
      toast.success(`${ecn.ecnNumber} raised`);
      refreshLists();
      closeForm();
      setViewingId(ecn.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The note could not be raised')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Drafts', value: formatNumber(summary?.drafts ?? 0), hint: 'Being written' },
    { label: 'Awaiting approval', value: formatNumber(summary?.awaitingApproval ?? 0), hint: 'Sent, not yet decided' },
    { label: 'To implement', value: formatNumber(summary?.awaitingImplementation ?? 0), hint: 'Approved; the BOM is not changed yet' },
    { label: 'Implemented', value: formatNumber(summary?.implementedThisMonth ?? 0), hint: 'This month' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Engineering Changes</h1>
          <p className="mt-1 text-sm text-gray-500">An approved BOM is changed through a change note: what changes and why, an approval, and then the BOM takes its next revision.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Raise Change Note
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by note number, title, BOM or product" aria-label="Search change notes" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-48')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Note</th>
                <th scope="col" className={thClass}>Change</th>
                <th scope="col" className={thClass}>BOM</th>
                <th scope="col" className={thClass}>Revision</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The change notes could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No change note matches' : 'No change notes yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Raise a note to change the components of an approved BOM.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.ecnNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.createdAt)} · {TYPES[row.changeType]}</p>
                    </td>
                    <td className="max-w-md px-4 py-3"><p className="truncate text-sm text-gray-900" title={row.title}>{row.title}</p></td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="text-sm text-gray-900">{row.bomNumber}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{row.productName}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {row.fromRevision}
                      {row.toRevision ? ` → ${row.toRevision}` : ''}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.ecnNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title="Raise Engineering Change Note" description="The note starts with the components as they are; the change is made on the note." size="lg">
        {formOpen && <RaiseForm bomId={bomFromLink} isPending={create.isPending} onCancel={closeForm} onSubmit={(input) => create.mutate(input)} />}
      </Modal>

      <Modal isOpen={!!viewingId && !formOpen} onClose={() => setViewingId(null)} title={viewing?.ecnNumber ?? 'Engineering Change Note'} size="2xl">
        {viewingId && <EcnPanel key={viewingId} ecnId={viewingId} canEdit={canEdit} canApprove={canApprove} onClose={() => setViewingId(null)} onChanged={refreshLists} />}
      </Modal>
    </div>
  );
};

export default EngineeringChanges;
